type LoanDebtAccountInput = {
  code?: unknown
  accountNumber?: unknown
  name?: unknown
  category?: unknown
  active?: unknown
}

type LoanDebtAccount = {
  code: string
  name: string
  category: string
  active: boolean
}

type ResolveLoanDebtAccountsArgs = {
  country?: unknown
  accounts?: LoanDebtAccountInput[]
  limit?: unknown
}

type ResolveLoanDebtAccountsResult = {
  jurisdiction: string
  options: LoanDebtAccount[]
  allOptions?: LoanDebtAccount[]
  reasonCode: string
}

;(function registerBankLoanDebtAccounts(scope: typeof globalThis) {
  const COUNTRY_TERMS: Record<string, RegExp> = {
    NO: /(?:lån|gjeld|kredittinstitusjon|pantelån|finansiering)/i,
    SE: /(?:lån|skuld|kreditinstitut|finansiering)/i,
    DK: /(?:lån|gæld|kreditinstitut|finansiering)/i,
  }
  const EXCLUDED_TERMS = /(?:eier|nærstående|aksjonær|personlig|konsern|tilknyttet|leverand|kreditor|kunde|skatt|avgift|mva|moms|lønn|lön|udbytte|utbytte|rente|kostnad|omkostning|egenkapital|eget kapital)/i
  const PREFERRED_TERMS = /(?:bank|kredittinstitusjon|kreditinstitut|pantelån|langfrist|långfrist)/i
  const cleanAccount = (account: LoanDebtAccountInput): LoanDebtAccount => ({
    code: String(account?.code || account?.accountNumber || '').trim(),
    name: String(account?.name || '').trim(),
    category: String(account?.category || '').trim(),
    active: account?.active !== false,
  })
  const isLoanDebtAccount = (account: LoanDebtAccount): boolean => /^[12]\d{3,4}$/.test(account.code)
  const isCommonLoanDebtAccount = (account: LoanDebtAccount): boolean => isLoanDebtAccount(account) && (
    account.category === 'asset'
    || account.category === 'equity-liability'
    || /^2\d{3,4}$/.test(account.code)
  )

  const resolveLoanDebtAccounts = ({ country, accounts = [], limit = 6 }: ResolveLoanDebtAccountsArgs = {}): ResolveLoanDebtAccountsResult => {
    const jurisdiction = String(country || '').trim().toUpperCase()
    const term = COUNTRY_TERMS[jurisdiction]
    if (!term) return { jurisdiction: jurisdiction || 'unknown', options: [], reasonCode: 'jurisdiction_not_supported' }
    const allOptions = [...new Map<string, LoanDebtAccount>((Array.isArray(accounts) ? accounts : [])
      .map(cleanAccount)
      .filter((account) => account.active && account.code && account.name && isLoanDebtAccount(account))
      .map((account) => [account.code, account])).values()]
      .sort((a, b) => a.code.localeCompare(b.code, jurisdiction === 'NO' ? 'nb-NO' : jurisdiction === 'SE' ? 'sv-SE' : 'da-DK'))
    const options = allOptions
      .filter((account) => isCommonLoanDebtAccount(account))
      .filter((account) => term.test(account.name) && !EXCLUDED_TERMS.test(account.name))
      .sort((a, b) => Number(!PREFERRED_TERMS.test(a.name)) - Number(!PREFERRED_TERMS.test(b.name)) || a.code.localeCompare(b.code, jurisdiction === 'NO' ? 'nb-NO' : jurisdiction === 'SE' ? 'sv-SE' : 'da-DK'))
      .slice(0, Math.max(1, Number(limit) || 6))
    return { jurisdiction, options, allOptions, reasonCode: options.length ? 'common_debt_accounts_found' : 'no_common_debt_accounts' }
  }

  scope.PlummeBankLoanDebt = { resolveLoanDebtAccounts }
})(typeof window !== 'undefined' ? window : globalThis)
